/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ButtonERP,
  ButtonFilterERP,
  CalendarPrimeReact,
  Checkbox,
  InputCurrency,
  SearchInput,
  TagERP,
} from "@finanedi.es/storybook"
import {
  faCircleCheck,
  faCircleXmark,
  faClock,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { AceptablesQueryParamsProps } from "api/Factura.service"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import {
  ESTADO_VERIFICACION_EFECTO,
  EstadoVerificacionEfectoType,
} from "api/Types/EstadoVerificacionEfecto"
import { AnimatePresence } from "framer-motion"
import { Dialog } from "primereact/dialog"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { frontendCurrency } from "utils/formatCurrency"
import { backendDateFormat, frontendDateFormat } from "utils/formatDate"

const FilterMenu = ({
  serviceQuery,
  sortingCriteria,
  skipCount,
  maxResultCount,
  isContactoPage,
  resetPagination,
}: {
  serviceQuery: $TSMigrationFixMe
  sortingCriteria?: SortingCriteria
  skipCount?: AceptablesQueryParamsProps["SkipCount"]
  maxResultCount?: AceptablesQueryParamsProps["MaxResultCount"]
  isContactoPage: boolean
  resetPagination: () => void
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const [keyword, setKeyword] = useState("")
  const [menuOpen, setMenuOpen] = useState(false)
  const [amount, setAmount] = useState<{
    min: number | undefined
    max: number | undefined
  }>({
    min: undefined,
    max: undefined,
  })
  const INIITAL_ESTADO_VERIFICACION = {
    [ESTADO_VERIFICACION_EFECTO.PENDIENTE]: false,
    [ESTADO_VERIFICACION_EFECTO.COMUNICADO]: false,
    [ESTADO_VERIFICACION_EFECTO.VERIFICADO_MANUALMENTE]: false,
    [ESTADO_VERIFICACION_EFECTO.NO_VERIFICADO]: false,
    [ESTADO_VERIFICACION_EFECTO.VERIFICADO]: false,
    [ESTADO_VERIFICACION_EFECTO.GENERANDO_EVIDENCIA]: false,
  }

  const [estadoVerificacion, setEstadoVerificacion] = useState<
    Record<EstadoVerificacionEfectoType, boolean>
  >(INIITAL_ESTADO_VERIFICACION)
  const [date, setDate] = useState<Record<string, string | undefined>>()
  const openFilters = () => {
    setMenuOpen(true)
  }
  const queryFixer = (queryParams: AceptablesQueryParamsProps) => {
    const facturasParams = {
      EmpresaId: queryParams.EmpresaId,
      Estados: queryParams.Estados,
      FechaEmisionLowerLimit: queryParams.FechaEmisionLowerLimit,
      FechaEmisionUpperLimit: queryParams.FechaEmisionUpperLimit,
      ImporteLowerLimit: queryParams.ImporteLowerLimit,
      ImporteUpperLimit: queryParams.ImporteUpperLimit,
      Keyword: queryParams.Keyword,
      MaxResultCount: queryParams.MaxResultCount,
      SkipCount: queryParams.SkipCount,
      SortingCriteria:
        queryParams.SortingCriteria?.property &&
        queryParams.SortingCriteria?.sort
          ? `${queryParams.SortingCriteria.property} ${queryParams.SortingCriteria.sort}`
          : "",
    }

    const fixedQueryParams = Object.fromEntries(
      Object.entries(facturasParams).filter(([_key, value]) => value) // eslint-disable-line @typescript-eslint/no-unused-vars
    )
    return fixedQueryParams
  }
  const handleQuery = () => {
    const estadosVerificacion = Object.entries(estadoVerificacion)
      .filter(([_key, value]) => value) // eslint-disable-line @typescript-eslint/no-unused-vars
      .flatMap(([key]) => key) as unknown as EstadoVerificacionEfectoType[]
    serviceQuery(
      queryFixer({
        FechaEmisionLowerLimit:
          date?.min && backendDateFormat(new Date(date.min)),
        FechaEmisionUpperLimit:
          date?.max && backendDateFormat(new Date(date.max)),
        ImporteLowerLimit: amount.min,
        ImporteUpperLimit: amount.max,
        Estados: estadosVerificacion.length > 0 ? estadosVerificacion : [],
        Keyword: keyword,
        SortingCriteria: {
          property: sortingCriteria?.property ?? "Verificacion.estado",
          sort: sortingCriteria?.sort ?? "ASC",
        },
        MaxResultCount: maxResultCount,
        SkipCount: skipCount,
      })
    )
  }

  const handleApplyFilters = () => {
    handleQuery()
    setMenuOpen(false)
  }
  const hideFilter = () => {
    setMenuOpen(!menuOpen)
  }
  const handleResetFilters = () => {
    setEstadoVerificacion(INIITAL_ESTADO_VERIFICACION)
    setAmount({ min: undefined, max: undefined })
    setDate({ min: undefined, max: undefined })
  }

  const toggleReviewedStatus = (
    keys: EstadoVerificacionEfectoType | EstadoVerificacionEfectoType[]
  ) => {
    const keysArray = Array.isArray(keys) ? keys : [keys]

    setEstadoVerificacion((prev) => {
      const updated = { ...prev }
      keysArray.forEach((key) => {
        updated[key] = !prev?.[key]
      })
      return updated
    })
  }

  const appliedFiltersTags = [
    {
      label: t("filtros.appliedTag.amountFrom"),
      value: amount?.min ? frontendCurrency(amount?.min) : undefined,
      action: () => setAmount({ ...amount, min: undefined }),
    },
    {
      label: t("filtros.appliedTag.amountTo"),
      value: amount?.max ? frontendCurrency(amount?.max) : undefined,
      action: () => setAmount({ ...amount, max: undefined }),
    },

    {
      label: `${t("filtros.appliedTag.dateFrom")}`,
      value: date?.min ? frontendDateFormat(new Date(date.min)) : undefined,
      action: () => setDate({ ...date, min: undefined }),
    },
    {
      label: `${t("filtros.appliedTag.dateTo")}`,
      value: date?.max ? frontendDateFormat(new Date(date.max)) : undefined,
      action: () => setDate({ ...date, max: undefined }),
    },
    {
      label: `${t("filterMenu.appliedTag.statusTag")}`,
      value:
        estadoVerificacion?.[ESTADO_VERIFICACION_EFECTO.PENDIENTE] ||
        estadoVerificacion?.[ESTADO_VERIFICACION_EFECTO.COMUNICADO]
          ? t("filterMenu.invoice.pendingLabel")
          : undefined,
      action: () => {
        toggleReviewedStatus(ESTADO_VERIFICACION_EFECTO.PENDIENTE)
        toggleReviewedStatus(ESTADO_VERIFICACION_EFECTO.COMUNICADO)
      },
    },
    {
      label: `${t("filterMenu.appliedTag.statusTag")}`,
      value:
        estadoVerificacion?.[ESTADO_VERIFICACION_EFECTO.VERIFICADO] ||
        estadoVerificacion?.[ESTADO_VERIFICACION_EFECTO.GENERANDO_EVIDENCIA]
          ? t("filterMenu.invoice.approvedLabel")
          : undefined,
      action: () => {
        toggleReviewedStatus(ESTADO_VERIFICACION_EFECTO.VERIFICADO)
        toggleReviewedStatus(ESTADO_VERIFICACION_EFECTO.GENERANDO_EVIDENCIA)
      },
    },
    {
      label: `${t("filterMenu.appliedTag.statusTag")}`,
      value: estadoVerificacion?.[ESTADO_VERIFICACION_EFECTO.NO_VERIFICADO]
        ? t("filterMenu.invoice.rejectedLabel")
        : undefined,
      action: () =>
        toggleReviewedStatus(ESTADO_VERIFICACION_EFECTO.NO_VERIFICADO),
    },
  ]
  const appliedFilters = appliedFiltersTags.filter((filter) => filter.value)

  useEffect(() => {
    resetPagination?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, appliedFilters?.length])

  useEffect(() => {
    handleQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sortingCriteria?.sort,
    sortingCriteria?.property,
    skipCount,
    maxResultCount,
    appliedFilters.length,
    keyword,
  ])

  return (
    <>
      <div className='tw-flex tw-flex-row tw-gap-3 tw-h-10'>
        <ButtonFilterERP
          isActive={appliedFilters.length > 0}
          numberActive={appliedFilters.length}
          onClick={openFilters}
          deleteFilter={handleResetFilters}
          filterButtonLabel={`${t("filtros.title")}`}
        />
        {isContactoPage && (
          <SearchInput
            placeholder={`${t("validaciones.placeholderSearch")}`}
            onChange={(ev: string) => {
              resetPagination?.()
              setKeyword(ev)
            }}
            inputValue={keyword}
            className='tw-h-10 tw-w-full sm:tw-w-72'
          />
        )}
      </div>

      <AnimatePresence>
        <Dialog
          breakpoints={{ "960px": "75vw" }}
          className='p-dialog-right'
          header={t("filtros.title")}
          visible={menuOpen}
          footer={
            <div className='tw-w-full tw-border-t-2 tw-pt-6'>
              <ButtonERP
                customize={{
                  type: "primary",
                  size: "md",
                }}
                className='!tw-w-full'
                onClick={handleApplyFilters}
                label={`${t("filtros.labelApply")}`}
              />
            </div>
          }
          onHide={() => {
            hideFilter()
          }}
          draggable={false}
          resizable={false}
          position='right'
          closable
          closeOnEscape
        >
          {!!appliedFilters?.length && (
            <div className='tw-mb-8'>
              <div className='flex tw- tw-mb-4 tw-justify-between'>
                <span className='tw-font-medium'>
                  {t("filterMenu.appliedTag.appliedFilters")}
                </span>
                <span
                  onClick={handleResetFilters}
                  className='tw-text-text-hyperlinks tw-cursor-pointer'
                >
                  {t("filterMenu.appliedTag.deleteAll")}
                </span>
              </div>
              <div className='tw-flex tw-flex-wrap tw-mb-8 tw-gap-2 tw-font-normal tw-text-xs'>
                {appliedFilters.map((filter) => (
                  <div
                    key={`${filter.label}-${filter.value}`}
                    onClick={filter.action}
                    className='tw-items-center tw-justify-between tw-cursor-pointer tw-bg-info-10 tw-text-info tw-rounded-md tw-border tw-border-info tw-py-1 tw-px-2'
                  >
                    <span>
                      {filter.label} {filter.value}
                    </span>
                    <FontAwesomeIcon
                      icon={["fal", "xmark"]}
                      className='tw-ml-2 tw-text-info'
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("filtros.importe.title")}
              </span>
            </div>
            <div className='tw-flex tw-flex-row tw-mb-8'>
              <div className='tw-flex tw-flex-col'>
                <label htmlFor='amountMin'>
                  {t("filtros.importe.fromLabel")} (€)
                </label>
                <InputCurrency
                  placeholder='0,00'
                  value={amount.min}
                  className='!tw-w-full !tw-h-10 !tw-rounded'
                  suffix='€'
                  min={0}
                  max={amount.max ?? Number.MAX_SAFE_INTEGER}
                  onChange={(value) => setAmount({ ...amount, min: value })}
                />
              </div>
              <div className='tw-py-3 tw-px-6 tw-items-baseline tw-mt-auto tw-mb-0'>
                -
              </div>
              <div className='tw-flex tw-flex-col'>
                <label htmlFor='amountMax'>
                  {t("filtros.importe.toLabel")} (€)
                </label>
                <InputCurrency
                  placeholder='0,00'
                  value={amount.max}
                  className='!tw-w-full !tw-h-10 !tw-rounded'
                  suffix='€'
                  min={amount?.min ?? 0}
                  max={Number.MAX_SAFE_INTEGER}
                  onChange={(value) => setAmount({ ...amount, max: value })}
                />
              </div>
            </div>
          </div>

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("filterMenu.invoice.status")}
              </span>
            </div>

            <div className='tw-flex tw-items-center tw-mb-4'>
              <Checkbox
                id='pending'
                className='tw-mr-4'
                isChecked={
                  estadoVerificacion?.[ESTADO_VERIFICACION_EFECTO.PENDIENTE] ||
                  estadoVerificacion?.[ESTADO_VERIFICACION_EFECTO.COMUNICADO]
                }
                onClick={() => {
                  toggleReviewedStatus([
                    ESTADO_VERIFICACION_EFECTO.PENDIENTE,
                    ESTADO_VERIFICACION_EFECTO.COMUNICADO,
                  ])
                }}
              />
              <TagERP
                value={t("filters.estadoRevision.pendingLabel")}
                color='alternativeOne'
                icon={<FontAwesomeIcon icon={faClock} />}
              />
            </div>
            <div className='tw-flex tw-items-center tw-mb-4'>
              <Checkbox
                id='validated'
                className='tw-mr-4'
                isChecked={
                  estadoVerificacion?.[ESTADO_VERIFICACION_EFECTO.VERIFICADO] ||
                  estadoVerificacion?.[
                    ESTADO_VERIFICACION_EFECTO.GENERANDO_EVIDENCIA
                  ]
                }
                onClick={() =>
                  toggleReviewedStatus([
                    ESTADO_VERIFICACION_EFECTO.VERIFICADO,
                    ESTADO_VERIFICACION_EFECTO.GENERANDO_EVIDENCIA,
                  ])
                }
              />
              <TagERP
                value={t("filters.estadoRevision.validateLabel")}
                color='success'
                icon={<FontAwesomeIcon icon={faCircleCheck} />}
              />
            </div>
            <div className='tw-flex tw-items-center tw-mb-4'>
              <Checkbox
                id='rejected'
                className='tw-mr-4'
                isChecked={
                  estadoVerificacion?.[ESTADO_VERIFICACION_EFECTO.NO_VERIFICADO]
                }
                onClick={() =>
                  toggleReviewedStatus(ESTADO_VERIFICACION_EFECTO.NO_VERIFICADO)
                }
              />
              <TagERP
                value={t("filters.estadoRevision.rejectedLabel")}
                color='danger'
                icon={<FontAwesomeIcon icon={faCircleXmark} />}
              />
            </div>
          </div>

          <div className='tw-flex tw-flex-row tw-mb-6'>
            <div className='tw-flex tw-flex-col'>
              <label htmlFor='fechaDesde'>
                {t("filterMenu.invoice.issueDate.since")}
              </label>
              <CalendarPrimeReact
                id='fechaDesde'
                name='fechaDesde'
                clearButtonClassName='!tw-hidden'
                value={date?.min ? new Date(date.min) : undefined}
                locale={language}
                onChange={(e) => {
                  const eventDate = e.target.value as unknown as string
                  setDate({
                    max:
                      date?.max && new Date(eventDate) > new Date(date?.max)
                        ? undefined
                        : date?.max,
                    min: eventDate,
                  })
                }}
                placeholder='dd/mm/aaaa'
                dateFormat='dd/mm/yy'
                className='tw-w-full'
                baseZIndex={2302}
                showIcon
                maxDate={date?.max ? new Date(date.max) : undefined}
              />
            </div>
            <div className='tw-py-3 tw-px-6 tw-items-baseline tw-mt-auto tw-mb-0'>
              -
            </div>
            <div className='tw-flex tw-flex-col'>
              <label htmlFor='fechaHasta'>
                {t("filterMenu.invoice.issueDate.to")}
              </label>
              <CalendarPrimeReact
                id='fechaHasta'
                name='fechaHasta'
                clearButtonClassName='!tw-hidden'
                value={date?.max ? new Date(date.max) : undefined}
                locale={language}
                onChange={(e) => {
                  const eventDate = e.target.value as unknown as string
                  setDate({
                    min:
                      date?.min && new Date(eventDate) < new Date(date?.min)
                        ? undefined
                        : date?.min,
                    max: eventDate,
                  })
                }}
                placeholder='dd/mm/aaaa'
                dateFormat='dd/mm/yy'
                className='tw-w-full'
                baseZIndex={2302}
                showIcon
                minDate={date?.min ? new Date(date.min) : undefined}
              />
            </div>
          </div>
        </Dialog>
      </AnimatePresence>
    </>
  )
}

export default FilterMenu
